import React from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Util} from 'yougile-util'
import {DataObjectId, GroupChat, Hub} from 'yougile-model'
import {Sync} from '@client/sync'
import {UserSettings} from '@client/user-settings'
import {Tracking} from '@common/tracking'
import {TrackingEvent} from '@common/tracking-events'
import {TaskChatQuickLinkList} from '@components/chat/chat-header/task-chat-quick-link-list'
import {AvatarGroupBase} from '@components/common/base/avatar'
import {IconButton} from '@components/common/base/buttons'
import {Icon} from '@components/common/base/icon'
import {useTaskFromHubWithStarredEffect} from '@components/my-tasks/task-item'
import {rightPanelStore} from '@components/right-panel/right-panel'
import {cn} from '@containers/html-utils'
import {withUpdaters} from '@containers/updaters'
import {SyncUpdater} from '@containers/updaters/sync-updater'
import {globals} from '@extensions/globals-base'
import {GroupChatProxy} from '@extensions/proxies/group-chat-proxy'
import {DivWithHint} from '../../common/hint/hint'
import {ChatNav} from './chat-nav'

interface Props {
  groupChat: GroupChatProxy
  chatHub: Hub
  edit: () => void
  chatId: DataObjectId
  companyId: DataObjectId
  notificationsEnabled: boolean
  toggleNotifications: () => void
}

const maxUsers = 7

const TwGroupChatHeader = tw.div`
  px-24
  pb-12
  flex
  flex-col
  gap-8
  shadow-border-b-1
  shadow-grey-l60
`

const TwRow = tw.div`
  flex
  gap-8
`

const TwHeading = tw(DivWithHint)`
  flex-1
  min-w-0
  text-18
  leading-6
  font-semibold
  break-words
`

const GroupChatHeaderBase = ({
  groupChat,
  edit,
  notificationsEnabled,
  toggleNotifications,
  chatHub,
  chatId,
  companyId
}: Props) => {
  const allUsersInChat = [...groupChat.users.values()].sort(
    (a, b) =>
      +(a.userId === globals.currentUserId) -
      +(b.userId === globals.currentUserId)
  )

  const chatStarred = useTaskFromHubWithStarredEffect(chatHub)
  const toggleStar = () => {
    UserSettings.setStarred(chatHub, !chatStarred?.starred, 'group')
  }

  return (
    <>
      <ChatNav />
      <TwGroupChatHeader>
        <TwRow>
          <Icon
            icon="IconUsers"
            size={16}
            color="secondary"
            className="flex-none w-24 h-24 -mx-4"
          />
          <TwHeading content={groupChat.chatName}>
            {Util.limitStr(groupChat.chatName, 150)}
          </TwHeading>

          <IconButton
            icon={chatStarred?.starred ? 'IconStarFilled' : 'IconStar'}
            color="secondary"
            size="small"
            wrapperSize="big"
            className={cn(
              'ml-auto flex-none -mx-4 mr-8',
              chatStarred?.starred && '!text-marker hover:!text-marker-l20'
            )}
            onClick={toggleStar}
            content={Lang.msg.taskPage.starHintGroupChat}
          />
          <IconButton
            icon={notificationsEnabled ? 'IconBell' : 'IconBellOff'}
            onClick={toggleNotifications}
            className="ml-auto flex-none -mx-4"
            size="small"
            wrapperSize="big"
            color="secondary"
            content={
              notificationsEnabled
                ? Lang.msg.groupChatMute
                : Lang.msg.groupChatUnmute
            }
          />
        </TwRow>
        <TwRow className="ml-24">
          <AvatarGroupBase
            users={allUsersInChat}
            size={24}
            maxUsers={maxUsers}
            outlineColor="grey-l100"
            onClick={edit}
            hideStatus
          />
          <IconButton
            icon="IconEdit"
            onClick={edit}
            size="small"
            wrapperSize="big"
            color="secondary"
            className="flex-none ml-4"
            content={Lang.msg.editGroupChat}
          />
        </TwRow>
      </TwGroupChatHeader>
      <TaskChatQuickLinkList chatId={chatId} companyId={companyId} />
    </>
  )
}

export const GroupChatHeader = withUpdaters(
  SyncUpdater({filter: '*', companyType: 'current', debounceTime: 500})
)(({chatId}: {chatId: DataObjectId}) => {
  const box = Sync.getCurrentSandbox()
  const hub = box.get(chatId) as Hub
  if (!hub) {
    return null
  }

  const groupChat = GroupChatProxy.create(box, hub)
  const edit = () => {
    rightPanelStore.setGroupChatEdit(true)
    void Tracking.send(TrackingEvent.groupChatEditClick)
  }

  const toggleNotifications = () => {
    if (!globals.currentUserId) {
      return
    }
    if (GroupChat.getParticipants(hub)[globals.currentUserId]) {
      void Tracking.send(TrackingEvent.groupChatUnsubscribe)
    } else {
      void Tracking.send(TrackingEvent.groupChatSubscribe)
    }

    GroupChat.toggleNotifiedWithoutCommit(hub, globals.currentUserId)
    hub.commit()
  }

  return (
    <GroupChatHeaderBase
      groupChat={groupChat}
      edit={edit}
      chatHub={hub}
      chatId={chatId}
      companyId={hub.companyId}
      notificationsEnabled={GroupChat.isNotified(hub, globals.currentUserId)}
      toggleNotifications={toggleNotifications}
    />
  )
})
